import { useConfirmation } from "src/sudokit/components/ConfirmationDialog/ConfirmationProvider";
import { useCallback, useEffect, useReducer, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../sudokit/components/Dialog";
import { useToast } from "../ToastWrapper";
import * as repairApi from "../../api/repair";
import type { RestoreStructureResult, StepId, StepResult } from "../../api/repair";
import { DiagnosisStep } from "./DiagnosisStep";
import { ExecuteStep } from "./ExecuteStep";
import { initialRepairState, repairReducer } from "./repairReducer";
import { buildDiagnosisReport, downloadText, parseUploadedBackup } from "./downloads";

type Props = {
  projectId: string;
  projectTitle?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

function errorMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === "string") return e;
  return "Unknown error";
}

function downloadTranscript(projectTitle: string | undefined, projectId: string, transcript: ReturnType<typeof initial>["transcript"]) {
  const header = `Repair Project Transcript\nProject: ${projectTitle || "Untitled"} (${projectId})\nRan at: ${new Date().toISOString()}\n\n`;
  const body = transcript
    .map((entry) => {
      const lines = [
        `## ${entry.title} (${entry.stepId})`,
        `At: ${entry.at}`,
        `Changes: ${entry.count}`,
        ...entry.changed.map((c) => `  - [${c.action}] ${c.title} (${c.id})`),
      ];
      return lines.join("\n");
    })
    .join("\n\n");
  downloadText(`repair-transcript-${projectId}-${Date.now()}.txt`, header + body);
}

function initial() {
  return initialRepairState;
}

export function RepairProjectModal({ projectId, projectTitle, open, onOpenChange }: Props) {
  const confirmAction = useConfirmation(`${projectId}:${open}`);
  const [state, dispatch] = useReducer(repairReducer, undefined, initial);
  const { setToast } = useToast();

  // Mirror of `state` that's always current. `callStep` and `handleApplyAll`
  // read from this instead of closure-captured state so sequential dispatches
  // (step-applied refreshes diagnosis; advanceReadiness flips step statuses)
  // are visible to the next iteration.
  const stateRef = useRef(state);
  stateRef.current = state;

  const runDiagnosis = useCallback(
    async (timestamp?: number | null) => {
      dispatch({ type: "start" });
      try {
        const diagnosis = await repairApi.diagnose(projectId, timestamp ?? undefined);
        dispatch({ type: "diagnose-success", diagnosis });
        if (timestamp != null) dispatch({ type: "set-pitr-timestamp", timestamp });
      } catch (e) {
        dispatch({ type: "diagnose-error", error: errorMessage(e) });
      }
    },
    [projectId]
  );

  const handleUploadBackup = useCallback(
    async (file: File | null) => {
      if (!file) {
        dispatch({ type: "set-uploaded-backup", backup: null });
        return;
      }
      try {
        const backup = await parseUploadedBackup(file, projectId);
        dispatch({ type: "set-uploaded-backup", backup });
        setToast(`Loaded backup: ${backup.name} (${backup.folderCount} folders, ${backup.documentCount} documents)`);
      } catch (e) {
        setToast(`❌ ${errorMessage(e)}`);
      }
    },
    [projectId, setToast]
  );

  const handleDownloadDiagnosisReport = useCallback(() => {
    if (!state.diagnosis) return;
    const folderStep = state.planSteps.find((s) => s.id === "restore-folders");
    const folderResult = (folderStep?.result as RestoreStructureResult | undefined) ?? null;
    const text = buildDiagnosisReport(projectTitle, projectId, state.diagnosis, state.planSteps, folderResult);
    downloadText(`repair-diagnosis-${projectId}-${Date.now()}.txt`, text);
  }, [projectId, projectTitle, state.diagnosis, state.planSteps]);

  // Downloads the raw backup JSON (projectId + contents + folders). For
  // uploaded backups it's already in memory; for automatic backups we fetch
  // the raw PITR snapshot. Staff uses this to preserve a backup before its
  // 7-day retention window expires.
  const handleDownloadBackupJson = useCallback(async () => {
    try {
      let raw: unknown;
      let filename: string;
      if (state.uploadedBackup) {
        raw = state.uploadedBackup.payload;
        filename = `backup-${projectId}-upload.json`;
      } else if (state.pitrTimestamp != null) {
        const response = await repairApi.fetchAutomaticBackupRaw(projectId, state.pitrTimestamp);
        raw = response.raw;
        filename = `backup-${projectId}-${state.pitrTimestamp}.json`;
      } else {
        setToast("Pick a backup source first.");
        return;
      }
      downloadText(filename, JSON.stringify(raw, null, 2), "application/json");
      setToast("Backup JSON downloaded.");
    } catch (e) {
      setToast(`❌ ${errorMessage(e)}`);
    }
  }, [projectId, state.uploadedBackup, state.pitrTimestamp, setToast]);

  useEffect(() => {
    if (!open) {
      dispatch({ type: "reset" });
    }
  }, [open]);

  // Auto-run diagnosis when the modal opens. The admin came here to see
  // what's wrong; making them click a second button adds no value.
  useEffect(() => {
    if (open && !state.diagnosis && !state.loading && !state.error) {
      runDiagnosis();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const callStep = useCallback(
    async (stepId: StepId, dryRun: boolean): Promise<StepResult> => {
      // Read from the ref so this function always sees current state, even
      // when called in a loop that dispatches between iterations.
      const current = stateRef.current;
      switch (stepId) {
        case "restore-missing":
          return await repairApi.restoreMissing(projectId, dryRun);
        case "remove-external":
          return await repairApi.removeExternal(projectId, dryRun);
        case "restore-folders": {
          // Only dry-run needs missingDocs (to place them in the preview tree).
          // The real apply path is per-step; missing docs are handled by
          // restore-missing separately.
          const missingDocs = dryRun
            ? (current.diagnosis?.missingFromTree ?? []).map((d) => ({ id: d.id, title: d.title }))
            : undefined;
          if (current.uploadedBackup) {
            return await repairApi.restoreStructure(projectId, {
              dryRun,
              backupData: current.uploadedBackup.payload,
              missingDocs,
            });
          }
          if (!current.diagnosis?.folders.backup.available) {
            throw new Error("No backup available to restore from — pick an automatic backup time or upload a backup JSON");
          }
          return await repairApi.restoreStructure(projectId, {
            dryRun,
            timestamp: current.pitrTimestamp ?? current.diagnosis.folders.backup.timestamp,
            missingDocs,
          });
        }
      }
    },
    [projectId]
  );

  // Silent dry-run fetch used to pre-load rich preview details for steps whose
  // plan-derived preview is a summary only (currently just restore-folders,
  // which needs server-computed tree + moves).
  const handleLoadPreview = useCallback(
    async (stepId: StepId) => {
      try {
        const result = await callStep(stepId, true);
        dispatch({ type: "step-preview-loaded", stepId, result });
      } catch {
        // Silent failure — coarse summary in step.description is still shown.
      }
    },
    [callStep]
  );

  // Single-click apply. The preview is already visible inline (either from the
  // plan-derived step.preview, or for restore-folders from an auto-loaded
  // dry-run), so there's no separate confirm dialog.
  const handleApply = useCallback(
    async (stepId: StepId) => {
      dispatch({ type: "step-started", stepId });
      try {
        const result = await callStep(stepId, false);
        if (result.changed.length === 0) {
          dispatch({ type: "step-skip", stepId });
          setToast(`Nothing to do for "${stepId}"; skipped.`);
        } else {
          dispatch({ type: "step-applied", stepId, result });
          setToast(`✅ ${stepId} applied — ${result.changed.length} change${result.changed.length === 1 ? "" : "s"}`);
          // After applying restore-folders, the in-memory preview (tree + moves)
          // is stale — it shows what WOULD happen from the prior state. Pull a
          // fresh dry-run so the Diagnose tree reflects current reality and
          // admin doesn't have to click Re-run.
          if (stepId === "restore-folders") {
            handleLoadPreview(stepId);
          }
        }
      } catch (e) {
        const msg = errorMessage(e);
        dispatch({ type: "step-error", stepId, error: msg });
        setToast(`❌ ${msg}`);
      }
    },
    [callStep, setToast, handleLoadPreview]
  );

  // Auto-load the restore-folders preview once, when the step is ready and
  // a backup source is picked. Other steps have their preview inline from
  // plan derivation; only restore-folders needs a server round-trip for
  // moves + tree detail.
  useEffect(() => {
    const folderStep = state.planSteps.find((s) => s.id === "restore-folders");
    if (!folderStep) return;
    if (folderStep.status !== "ready" && folderStep.status !== "pending") return;
    if (folderStep.result) return;
    handleLoadPreview("restore-folders");
    // We only want to re-run when the step's identity changes (status/result),
    // not every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.planSteps, state.uploadedBackup, state.pitrTimestamp, handleLoadPreview]);

  const handleApplyAll = useCallback(async () => {
    // Snapshot the initial queue for the confirm message, but re-read from
    // stateRef each iteration so we respect status changes (e.g. an earlier
    // step auto-skipped with changed.length === 0 won't be retried) and the
    // freshly-audited diagnosis flows into subsequent callStep calls.
    const initialQueue = stateRef.current.planSteps.filter(s => s.status === "ready" || s.status === "pending");
    if (initialQueue.length === 0) return;
    await confirmAction({
      title: "Apply project repairs",
      message: `Apply all ${initialQueue.length} remaining step${initialQueue.length === 1 ? "" : "s"}? Each step runs sequentially. A failure mid-chain will stop execution; earlier steps will remain applied.`,
      actionLabel: "Apply all steps",
      destructive: false
    }, async () => {
      let applied = 0;
      for (const queued of initialQueue) {
        const fresh = stateRef.current.planSteps.find(s => s.id === queued.id);
        // Skip if an earlier iteration's audit refresh already resolved this
        // step (e.g. it's now "complete" or "skipped").
        if (!fresh || fresh.status !== "ready" && fresh.status !== "pending") continue;
        dispatch({
          type: "step-started",
          stepId: queued.id
        });
        try {
          const result = await callStep(queued.id, false);
          if (result.changed.length === 0) {
            dispatch({
              type: "step-skip",
              stepId: queued.id
            });
          } else {
            dispatch({
              type: "step-applied",
              stepId: queued.id,
              result
            });
            applied++;
          }
        } catch (e) {
          const msg = errorMessage(e);
          dispatch({
            type: "step-error",
            stepId: queued.id,
            error: msg
          });
          setToast(`❌ ${msg}`);
          return;
        }
      }
      setToast(`✅ Applied ${applied} step${applied === 1 ? "" : "s"}`);
    });
  }, [callStep, setToast, confirmAction]);

  const handleExportTranscript = useCallback(() => {
    downloadTranscript(projectTitle, projectId, state.transcript);
  }, [projectId, projectTitle, state.transcript]);

  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Repair Project — {projectTitle || projectId}</DialogTitle>
          <p className="text-sm text-gray-500 mt-1">
            Diagnose the project, then apply repairs. Each step shows exactly what it will change before you apply. Admin only.
          </p>
        </DialogHeader>

        <div className="mt-4">
          {state.phase === "diagnose" && (
            <DiagnosisStep
              diagnosis={state.diagnosis}
              loading={state.loading}
              error={state.error}
              uploadedBackup={state.uploadedBackup}
              pitrTimestamp={state.pitrTimestamp}
              folderStepResult={(state.planSteps.find((s) => s.id === "restore-folders")?.result as RestoreStructureResult | undefined) ?? null}
              onRun={runDiagnosis}
              onUploadBackup={handleUploadBackup}
              onDownloadReport={handleDownloadDiagnosisReport}
              onDownloadBackupJson={handleDownloadBackupJson}
              onContinue={() => dispatch({ type: "goto-execute" })}
            />
          )}
          {(state.phase === "execute" || state.phase === "done") && (
            <ExecuteStep
              state={state}
              onApply={handleApply}
              onSkip={(stepId) => dispatch({ type: "step-skip", stepId })}
              onApplyAll={handleApplyAll}
              onExportTranscript={handleExportTranscript}
              onBack={() => dispatch({ type: "goto-diagnose" })}
              onClose={close}
              onFinish={() => dispatch({ type: "finish" })}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
